import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApi, identityApiRef } from '@backstage/core-plugin-api';
import { PageShell } from '../components/PageShell';
import { KindIcon } from '../components/KindIcon';
import { EnvVarsEditor, type EnvVarRow } from '../components/EnvVarsEditor';
import { DepartmentSelect } from '../components/DepartmentSelect';
import { colors, cardStyle } from '../theme';
import { CREATE_KIND_CONFIG } from '../types';
import type { CreateKind, WorkflowStatus } from '../types';
import {
  CREATE_FLOW_FIELDS,
  PROGRESS_STAGES,
  PROGRESS_STAGES_EXISTING_REPO,
  stageIndexForStatus,
} from '../createFlowConfig';
import { useIdentity } from '../useIdentity';
import { sanitizeName } from '../nameUtils';
import { inputStyle, labelStyle, Field, ProgressStepper } from '../formPrimitives';
import {
  createAndDeployApplication,
  createAndDeployFullStackApplication,
  deployExistingRepository,
  getWorkflowStatus,
  checkAppNameAvailability,
  inspectExistingRepository,
  type RepoInspection,
} from '../api';

/** owner/repo — the only shape GitHub itself accepts for a full repository
 * name, and all cloneRepositoryActivity needs. */
const REPO_FULL_NAME_PATTERN = /^[\w.-]+\/[\w.-]+$/;

export function CreateFlowPage({ kind }: { kind: Exclude<CreateKind, 'compose'> }) {
  const navigate = useNavigate();
  const { displayName } = useIdentity();
  const identityApi = useApi(identityApiRef);
  const kindConfig = CREATE_KIND_CONFIG[kind];
  const fields = CREATE_FLOW_FIELDS[kind];

  const [name, setName] = useState('');
  const [department, setDepartment] = useState('');
  const [runtime, setRuntime] = useState(fields.runtimeOptions.find((o) => o.enabled)?.value ?? fields.runtimeOptions[0].value);
  const [modelProvider, setModelProvider] = useState(fields.modelProviderOptions?.[0]?.value ?? '');
  const [environment, setEnvironment] = useState(fields.environmentOptions[0].value);
  const [needs, setNeeds] = useState<Set<string>>(new Set());
  const [schedule, setSchedule] = useState('0 2 * * *');
  const [envVarRows, setEnvVarRows] = useState<EnvVarRow[]>([]);
  const [status, setStatus] = useState<WorkflowStatus>({ phase: 'idle' });
  const pollTimer = useRef<ReturnType<typeof setTimeout>>();

  // Fullstack always creates two fresh repos (backend + frontend) — deploying
  // from a single existing repo doesn't map onto that shape, so the toggle
  // below only ever renders for single-template kinds.
  const [source, setSource] = useState<'template' | 'existing'>('template');
  const [repoFullName, setRepoFullName] = useState('');
  const [ref, setRef] = useState('');
  // An existing repo's real listen port/healthcheck path can be anything —
  // unlike a golden template, there is no guarantee it matches this kind's
  // fixed `fields.defaultPort`/'/health' convention. Real bug found live: an
  // ai-agent-python repo (port 8000) deployed through the "Frontend App" kind
  // (port 3000) came up exited/unhealthy because Coolify healthchecked the
  // wrong port. Prefilled from the kind's defaults, but editable only here.
  const [existingPort, setExistingPort] = useState(String(fields.defaultPort));
  const [existingHealthcheck, setExistingHealthcheck] = useState('/health');

  type NameCheckState = { phase: 'idle' | 'checking' | 'available' | 'taken' | 'error'; repoName?: string };
  const [nameCheck, setNameCheck] = useState<NameCheckState>({ phase: 'idle' });
  const nameCheckTimer = useRef<ReturnType<typeof setTimeout>>();

  type RepoCheckState = { phase: 'idle' | 'checking' | 'done' | 'error'; inspection?: RepoInspection };
  const [repoCheck, setRepoCheck] = useState<RepoCheckState>({ phase: 'idle' });
  const repoCheckTimer = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => () => clearTimeout(pollTimer.current), []);

  // Live GitHub name-availability check, debounced — no database (see
  // platform-controller/src/applications/github-availability.ts), just an
  // earlier, quicker version of the same check the backend already does at
  // submit time, so a taken name is caught before the user even clicks
  // Create & Deploy. Meaningless when deploying an existing repo (no new repo
  // is created, so there is no name to collide), so skipped entirely there.
  useEffect(() => {
    clearTimeout(nameCheckTimer.current);
    if (source === 'existing') {
      setNameCheck({ phase: 'idle' });
      return;
    }
    const sanitized = sanitizeName(name);
    if (!sanitized) {
      setNameCheck({ phase: 'idle' });
      return;
    }
    setNameCheck({ phase: 'checking' });
    nameCheckTimer.current = setTimeout(async () => {
      try {
        const result = await checkAppNameAvailability(identityApi, sanitized, department || undefined);
        setNameCheck({ phase: result.available ? 'available' : 'taken', repoName: result.repoName });
      } catch {
        // A failed check shouldn't block the user — the real check still
        // happens for real at submit time either way.
        setNameCheck({ phase: 'error' });
      }
    }, 500);
    return () => clearTimeout(nameCheckTimer.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [name, source, department]);

  // Live "does this repo actually look like what's selected below" precheck,
  // debounced — always runs for a valid repo on the "Existing GitHub repo"
  // path (see repo-inspection.ts on the server for why: a real deploy went
  // exited/unhealthy because nothing warned that the picked kind's assumed
  // port/healthcheck didn't match what the repo actually served).
  useEffect(() => {
    clearTimeout(repoCheckTimer.current);
    const trimmed = repoFullName.trim();
    if (source !== 'existing' || !REPO_FULL_NAME_PATTERN.test(trimmed)) {
      setRepoCheck({ phase: 'idle' });
      return;
    }
    setRepoCheck({ phase: 'checking' });
    repoCheckTimer.current = setTimeout(async () => {
      try {
        const inspection = await inspectExistingRepository(identityApi, trimmed, ref.trim() || undefined);
        setRepoCheck({ phase: 'done', inspection });
      } catch {
        // A failed check must not silently allow a bad deploy through, but it
        // also shouldn't hard-block on a transient GitHub/network blip —
        // treat as "couldn't verify", same as an undetectable repo.
        setRepoCheck({ phase: 'error' });
      }
    }, 500);
    return () => clearTimeout(repoCheckTimer.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [repoFullName, ref, source]);

  const expectedRuntime = fields.runtimeToManifestRuntime[runtime];
  const inspection = repoCheck.phase === 'done' ? repoCheck.inspection : undefined;
  const repoTypeMismatch = Boolean(
    inspection?.detectedApplicationType && inspection.detectedApplicationType !== kind,
  );
  const repoRuntimeMismatch = Boolean(
    inspection?.detectedRuntime && inspection.detectedRuntime !== expectedRuntime,
  );
  // Blocks submit — see canSubmit below — only when the precheck actually
  // detected something (platform.yaml or the file-presence heuristic), never
  // on an undetectable repo, which would be indistinguishable from every
  // false positive this check could produce.
  const repoMismatch = repoTypeMismatch || repoRuntimeMismatch;

  const toggleNeed = (key: string) => {
    setNeeds((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const poll = useCallback(
    (workflowId: string) => {
      const tick = async () => {
        try {
          const res = await getWorkflowStatus(identityApi, workflowId);
          if (res.result) {
            setStatus({ phase: 'done', result: res.result });
            return;
          }
          setStatus({ phase: 'polling', workflowId, status: res.status });
          pollTimer.current = setTimeout(tick, 2000);
        } catch (err) {
          setStatus({ phase: 'error', message: (err as Error).message });
        }
      };
      tick();
    },
    [identityApi],
  );

  const onSubmit = async () => {
    const sanitizedName = sanitizeName(name);
    if (!sanitizedName) return;
    setStatus({ phase: 'submitting' });

    const extraEnvVars: Record<string, string> = {};
    if (fields.hasModelProvider) extraEnvVars.MUMZWORLD_MODEL_PROVIDER = modelProvider;
    if (fields.hasSchedule) extraEnvVars.MUMZWORLD_CRON_SCHEDULE = schedule;
    for (const row of envVarRows) {
      if (row.key.trim()) extraEnvVars[row.key.trim()] = row.value;
    }

    if (kind === 'fullstack') {
      const baseManifest = (application: string, appKind: 'backend' | 'frontend', runtimeValue: 'node' | 'static') => ({
        apiVersion: 'platform.mumzworld.com/v1',
        kind: 'ApplicationInfrastructure',
        metadata: { application, owner: displayName, branch: 'development', department },
        environment: { type: environment },
        application: { type: appKind, runtime: runtimeValue, healthcheck: '/health', port: fields.defaultPort },
        compute: {},
        aws: { s3: [], sqs: [], dynamodb: [], secrets: [] },
        network: { public: false },
        observability: {},
        deployment: {},
      });

      const backendRawManifest = {
        ...baseManifest(`${sanitizedName}-api`, 'backend', 'node'),
        aws: {
          s3: needs.has('S3') ? [{ name: `${sanitizedName}-data`, versioning: false }] : [],
          sqs: needs.has('SQS') ? [{ name: `${sanitizedName}-queue`, dlq: false }] : [],
          dynamodb: needs.has('DynamoDB') ? [{ name: `${sanitizedName}-state`, billingMode: 'PAY_PER_REQUEST' }] : [],
          database: needs.has('Database') ? { engine: 'postgres', size: 'small', public: false } : undefined,
          secrets: needs.has('Secrets') ? [{ name: 'APP_SECRET' }] : [],
        },
      };
      // The frontend half is a static bundle with the backend's URL baked
      // in at build time — it never gets its own AWS resources.
      const frontendRawManifest = baseManifest(`${sanitizedName}-web`, 'frontend', 'static');

      try {
        const { workflowId } = await createAndDeployFullStackApplication(identityApi, {
          backendRawManifest,
          frontendRawManifest,
          actorRole: 'DEVELOPER',
          ...(Object.keys(extraEnvVars).length > 0 ? { extraEnvVars } : {}),
        });
        poll(workflowId);
      } catch (err) {
        setStatus({ phase: 'error', message: (err as Error).message });
      }
      return;
    }

    const rawManifest = {
      apiVersion: 'platform.mumzworld.com/v1',
      kind: 'ApplicationInfrastructure',
      metadata: { application: sanitizedName, owner: displayName, branch: 'development', department },
      environment: { type: environment },
      application: {
        type: kind,
        runtime: fields.runtimeToManifestRuntime[runtime],
        healthcheck: source === 'existing' ? existingHealthcheck.trim() || '/health' : '/health',
        port: source === 'existing' ? Number(existingPort) || fields.defaultPort : fields.defaultPort,
      },
      compute: {},
      aws: {
        s3: needs.has('S3') ? [{ name: `${sanitizedName}-data`, versioning: false }] : [],
        sqs: needs.has('SQS') || needs.has('Queue') ? [{ name: `${sanitizedName}-queue`, dlq: false }] : [],
        dynamodb: needs.has('DynamoDB') ? [{ name: `${sanitizedName}-state`, billingMode: 'PAY_PER_REQUEST' }] : [],
        database: needs.has('Database') ? { engine: 'postgres', size: 'small', public: false } : undefined,
        secrets: needs.has('Secrets') ? [{ name: 'APP_SECRET' }] : [],
      },
      network: { public: false },
      observability: {},
      deployment: {},
    };

    try {
      const { workflowId } =
        source === 'existing'
          ? await deployExistingRepository(identityApi, {
              rawManifest,
              actorRole: 'DEVELOPER',
              repoFullName: repoFullName.trim(),
              ...(ref.trim() ? { ref: ref.trim() } : {}),
              ...(Object.keys(extraEnvVars).length > 0 ? { extraEnvVars } : {}),
            })
          : await createAndDeployApplication(identityApi, {
              rawManifest,
              actorRole: 'DEVELOPER',
              template: (fields.runtimeToTemplate?.[runtime] ?? kindConfig.template)!,
              deploy: true,
              ...(Object.keys(extraEnvVars).length > 0 ? { extraEnvVars } : {}),
            });
      poll(workflowId);
    } catch (err) {
      setStatus({ phase: 'error', message: (err as Error).message });
    }
  };

  const repoFullNameValid = REPO_FULL_NAME_PATTERN.test(repoFullName.trim());
  const existingPortValid = Number.isInteger(Number(existingPort)) && Number(existingPort) > 0;
  const canSubmit =
    Boolean(sanitizeName(name)) &&
    Boolean(department) &&
    (source === 'template'
      ? nameCheck.phase !== 'taken'
      : repoFullNameValid && existingPortValid && !repoMismatch);
  const isBusy = status.phase === 'submitting' || status.phase === 'polling';

  return (
    <PageShell>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 24 }}>
        <KindIcon kind={kind} size={40} />
        <h1 style={{ fontSize: 20, fontWeight: 700, color: colors.text, margin: 0 }}>{kindConfig.title}</h1>
      </div>

      <div style={{ ...cardStyle, padding: 28, maxWidth: 560 }}>
        {status.phase === 'idle' || status.phase === 'submitting' || status.phase === 'error' ? (
          <>
            {status.phase === 'error' && (
              <div role="alert" style={{ marginBottom: 16, color: colors.danger, fontSize: 14 }}>
                {status.message}
              </div>
            )}
            <DepartmentSelect value={department} onChange={setDepartment} />

            {kind !== 'fullstack' && (
              <Field>
                <span style={labelStyle}>Source</span>
                <div style={{ display: 'flex', gap: 8 }}>
                  {(['template', 'existing'] as const).map((opt) => (
                    <button
                      key={opt}
                      type="button"
                      onClick={() => setSource(opt)}
                      aria-pressed={source === opt}
                      style={{
                        flex: 1,
                        padding: '9px 0',
                        borderRadius: 8,
                        border: `1px solid ${source === opt ? colors.primary : colors.border}`,
                        background: source === opt ? colors.primary : colors.surface,
                        color: source === opt ? '#fff' : colors.text,
                        fontSize: 13,
                        fontWeight: 600,
                        cursor: 'pointer',
                      }}
                    >
                      {opt === 'template' ? 'New from template' : 'Existing GitHub repo'}
                    </button>
                  ))}
                </div>
              </Field>
            )}

            {source === 'existing' && (
              <>
                <Field>
                  <label style={labelStyle} htmlFor="repo-full-name">
                    GitHub repository
                  </label>
                  <input
                    id="repo-full-name"
                    style={inputStyle}
                    value={repoFullName}
                    onChange={(e) => setRepoFullName(e.target.value)}
                    placeholder="mumzworld-tech/some-existing-repo"
                    aria-label="GitHub repository"
                  />
                  {repoFullName.trim() && !repoFullNameValid && (
                    <div style={{ fontSize: 12, color: colors.danger, marginTop: 4 }}>
                      Expected "owner/repo"
                    </div>
                  )}
                  <div style={{ fontSize: 12, color: colors.textMuted, marginTop: 4 }}>
                    Cloned read-only to build the image — never modified or pushed to.
                  </div>
                </Field>
                <Field>
                  <label style={labelStyle} htmlFor="repo-ref">
                    Branch
                  </label>
                  <input
                    id="repo-ref"
                    style={inputStyle}
                    value={ref}
                    onChange={(e) => setRef(e.target.value)}
                    placeholder="Defaults to the repo's default branch"
                    aria-label="Branch"
                  />
                </Field>

                {repoFullNameValid && (
                  <Field>
                    {repoCheck.phase === 'checking' && (
                      <div style={{ fontSize: 12, color: colors.textMuted }}>Checking tech stack…</div>
                    )}
                    {repoCheck.phase === 'error' && (
                      <div style={{ fontSize: 12, color: colors.textMuted }}>
                        Couldn't verify the tech stack automatically — double-check port/health check below.
                      </div>
                    )}
                    {repoCheck.phase === 'done' && inspection?.source === 'none' && (
                      <div style={{ fontSize: 12, color: colors.textMuted }}>
                        Couldn't detect the tech stack automatically — double-check port/health check below.
                      </div>
                    )}
                    {repoCheck.phase === 'done' && inspection && inspection.source !== 'none' && !repoMismatch && (
                      <div style={{ fontSize: 12, color: colors.success }}>
                        ✓ Detected {inspection.detectedApplicationType ?? kind} / {inspection.detectedRuntime ?? expectedRuntime} —
                        matches {kindConfig.title}
                      </div>
                    )}
                    {repoCheck.phase === 'done' && repoMismatch && (
                      <div
                        role="alert"
                        style={{
                          fontSize: 13,
                          color: colors.danger,
                          background: `${colors.danger}14`,
                          border: `1px solid ${colors.danger}`,
                          borderRadius: 8,
                          padding: '10px 12px',
                        }}
                      >
                        <strong>This can't be deployed as {kindConfig.title}.</strong> This repo looks like a{' '}
                        {inspection?.detectedApplicationType ?? inspection?.detectedRuntime} app
                        {inspection?.detectedRuntime ? ` (${inspection.detectedRuntime})` : ''}, detected via{' '}
                        {inspection?.source === 'platform.yaml' ? 'its platform.yaml' : 'its files'} — deploying it
                        here would likely come up unhealthy (wrong runtime/port/healthcheck), the same way a real
                        deploy of this repo once did. Switch to the matching Create flow instead.
                      </div>
                    )}
                  </Field>
                )}

                <Field>
                  <label style={labelStyle} htmlFor="existing-port">
                    Port
                  </label>
                  <input
                    id="existing-port"
                    style={inputStyle}
                    type="number"
                    value={existingPort}
                    onChange={(e) => setExistingPort(e.target.value)}
                    aria-label="Port"
                  />
                  <div style={{ fontSize: 12, color: colors.textMuted, marginTop: 4 }}>
                    The port this repo's app actually listens on — an existing repo isn't
                    guaranteed to match this kind's usual default.
                  </div>
                </Field>
                <Field>
                  <label style={labelStyle} htmlFor="existing-healthcheck">
                    Health check path
                  </label>
                  <input
                    id="existing-healthcheck"
                    style={inputStyle}
                    value={existingHealthcheck}
                    onChange={(e) => setExistingHealthcheck(e.target.value)}
                    placeholder="/health"
                    aria-label="Health check path"
                  />
                </Field>
              </>
            )}

            <Field>
              <label style={labelStyle} htmlFor="app-name">
                {fields.nameLabel}
              </label>
              <input
                id="app-name"
                style={inputStyle}
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder={fields.namePlaceholder}
                aria-label={fields.nameLabel}
              />
              {nameCheck.phase === 'checking' && (
                <div style={{ fontSize: 12, color: colors.textMuted, marginTop: 4 }}>Checking availability…</div>
              )}
              {nameCheck.phase === 'available' && (
                <div style={{ fontSize: 12, color: colors.success, marginTop: 4 }}>✓ Available</div>
              )}
              {nameCheck.phase === 'taken' && (
                <div style={{ fontSize: 12, color: colors.danger, marginTop: 4 }}>
                  ✗ This name is already taken — try another
                </div>
              )}
            </Field>

            <Field>
              <label style={labelStyle} htmlFor="app-runtime">
                {fields.runtimeLabel}
              </label>
              <select
                id="app-runtime"
                style={inputStyle}
                value={runtime}
                onChange={(e) => setRuntime(e.target.value)}
                aria-label={fields.runtimeLabel}
              >
                {fields.runtimeOptions.map((opt) => (
                  <option key={opt.value} value={opt.value} disabled={!opt.enabled}>
                    {opt.label}
                    {!opt.enabled ? ' (coming soon)' : ''}
                  </option>
                ))}
              </select>
            </Field>

            {fields.hasModelProvider && (
              <Field>
                <label style={labelStyle} htmlFor="model-provider">
                  Model Provider
                </label>
                <select
                  id="model-provider"
                  style={inputStyle}
                  value={modelProvider}
                  onChange={(e) => setModelProvider(e.target.value)}
                  aria-label="Model Provider"
                >
                  {fields.modelProviderOptions?.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </Field>
            )}

            {fields.hasSchedule && (
              <Field>
                <label style={labelStyle} htmlFor="schedule">
                  Schedule
                </label>
                <input
                  id="schedule"
                  style={inputStyle}
                  value={schedule}
                  onChange={(e) => setSchedule(e.target.value)}
                  placeholder="0 2 * * *"
                  aria-label="Schedule"
                />
              </Field>
            )}

            <Field>
              <label style={labelStyle} htmlFor="environment">
                Environment
              </label>
              <select
                id="environment"
                style={inputStyle}
                value={environment}
                onChange={(e) => setEnvironment(e.target.value)}
                aria-label="Environment"
              >
                {fields.environmentOptions.map((opt) => (
                  <option key={opt.value} value={opt.value} disabled={!opt.enabled}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </Field>

            {fields.awsNeeds.length > 0 && (
              <Field>
                <span style={labelStyle}>Needs</span>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {fields.awsNeeds.map((need) => (
                    <label key={need.key} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14 }}>
                      <input
                        type="checkbox"
                        checked={needs.has(need.key)}
                        onChange={() => toggleNeed(need.key)}
                        aria-label={need.label}
                      />
                      {need.label}
                    </label>
                  ))}
                </div>
              </Field>
            )}

            <Field>
              <span style={labelStyle}>Environment Variables</span>
              <div style={{ fontSize: 12, color: colors.textMuted, marginBottom: 8 }}>
                API keys, a LiteLLM/OpenRouter URL, or anything else your app needs at runtime.
              </div>
              <EnvVarsEditor rows={envVarRows} onChange={setEnvVarRows} />
            </Field>

            <button
              type="button"
              onClick={onSubmit}
              disabled={!canSubmit || isBusy}
              style={{
                width: '100%',
                padding: '11px 0',
                borderRadius: 8,
                border: 'none',
                background: !canSubmit || isBusy ? colors.borderLight : colors.primary,
                color: !canSubmit || isBusy ? colors.textMuted : '#fff',
                fontSize: 14,
                fontWeight: 600,
                cursor: !canSubmit || isBusy ? 'default' : 'pointer',
              }}
            >
              {status.phase === 'submitting'
                ? 'Starting…'
                : source === 'existing'
                  ? 'Deploy Existing Repo'
                  : 'Create & Deploy'}
            </button>
          </>
        ) : status.phase === 'polling' ? (
          <ProgressStepper
            stages={source === 'existing' ? PROGRESS_STAGES_EXISTING_REPO : PROGRESS_STAGES}
            currentStage={stageIndexForStatus(status.status)}
          />
        ) : status.phase === 'done' ? (
          (() => {
            const result = status.result;
            const finalStatus = String(result?.status ?? '');
            if (finalStatus === 'DEPLOYED' && kind === 'fullstack') {
              const frontendUrl = String(result?.frontendUrl ?? '');
              const backendUrl = String(result?.backendUrl ?? '');
              return (
                <div>
                  <div style={{ fontSize: 18, fontWeight: 700, color: colors.text, marginBottom: 4 }}>
                    {String(result?.frontendRepoFullName ?? name).split('/').pop()?.replace(/-web$/, '')}
                  </div>
                  <div style={{ marginBottom: 16 }}>
                    <span style={{ color: colors.success, fontWeight: 600, fontSize: 14 }}>● Running</span>
                  </div>
                  {frontendUrl && (
                    <div style={{ marginBottom: 10, fontSize: 14 }}>
                      <span style={{ color: colors.textMuted, marginRight: 6 }}>Frontend:</span>
                      <a href={frontendUrl} target="_blank" rel="noreferrer" style={{ color: colors.primary }}>
                        {frontendUrl}
                      </a>
                    </div>
                  )}
                  {backendUrl && (
                    <div style={{ marginBottom: 20, fontSize: 14 }}>
                      <span style={{ color: colors.textMuted, marginRight: 6 }}>Backend:</span>
                      <a href={backendUrl} target="_blank" rel="noreferrer" style={{ color: colors.primary }}>
                        {backendUrl}
                      </a>
                    </div>
                  )}
                  <div style={{ display: 'flex', gap: 12 }}>
                    {frontendUrl && (
                      <a
                        href={frontendUrl}
                        target="_blank"
                        rel="noreferrer"
                        style={{
                          padding: '10px 18px',
                          borderRadius: 8,
                          background: colors.primary,
                          color: '#fff',
                          fontSize: 14,
                          fontWeight: 600,
                          textDecoration: 'none',
                        }}
                      >
                        Open Application
                      </a>
                    )}
                    <button
                      type="button"
                      onClick={() => navigate('/applications')}
                      style={{
                        padding: '10px 18px',
                        borderRadius: 8,
                        border: `1px solid ${colors.border}`,
                        background: colors.surface,
                        color: colors.text,
                        fontSize: 14,
                        fontWeight: 600,
                        cursor: 'pointer',
                      }}
                    >
                      View Details
                    </button>
                    <button
                      type="button"
                      onClick={() => navigate('/')}
                      style={{
                        padding: '10px 18px',
                        borderRadius: 8,
                        border: `1px solid ${colors.border}`,
                        background: colors.surface,
                        color: colors.text,
                        fontSize: 14,
                        fontWeight: 600,
                        cursor: 'pointer',
                      }}
                    >
                      Create New
                    </button>
                  </div>
                </div>
              );
            }
            if (finalStatus === 'DEPLOYED') {
              const liveUrl = String(result?.liveUrl ?? '');
              return (
                <div>
                  <div style={{ fontSize: 18, fontWeight: 700, color: colors.text, marginBottom: 4 }}>
                    {String(result?.repoFullName ?? name).split('/').pop()}
                  </div>
                  <div style={{ marginBottom: 12 }}>
                    <span style={{ color: colors.success, fontWeight: 600, fontSize: 14 }}>● Running</span>
                  </div>
                  {liveUrl && (
                    <div style={{ marginBottom: 20, fontSize: 14 }}>
                      <a href={liveUrl} target="_blank" rel="noreferrer" style={{ color: colors.primary }}>
                        {liveUrl}
                      </a>
                    </div>
                  )}
                  <div style={{ display: 'flex', gap: 12 }}>
                    {liveUrl && (
                      <a
                        href={liveUrl}
                        target="_blank"
                        rel="noreferrer"
                        style={{
                          padding: '10px 18px',
                          borderRadius: 8,
                          background: colors.primary,
                          color: '#fff',
                          fontSize: 14,
                          fontWeight: 600,
                          textDecoration: 'none',
                        }}
                      >
                        Open Application
                      </a>
                    )}
                    <button
                      type="button"
                      onClick={() => navigate('/applications')}
                      style={{
                        padding: '10px 18px',
                        borderRadius: 8,
                        border: `1px solid ${colors.border}`,
                        background: colors.surface,
                        color: colors.text,
                        fontSize: 14,
                        fontWeight: 600,
                        cursor: 'pointer',
                      }}
                    >
                      View Details
                    </button>
                    <button
                      type="button"
                      onClick={() => navigate('/')}
                      style={{
                        padding: '10px 18px',
                        borderRadius: 8,
                        border: `1px solid ${colors.border}`,
                        background: colors.surface,
                        color: colors.text,
                        fontSize: 14,
                        fontWeight: 600,
                        cursor: 'pointer',
                      }}
                    >
                      Create New
                    </button>
                  </div>
                </div>
              );
            }
            return (
              <div>
                <div style={{ fontSize: 16, fontWeight: 700, color: colors.danger, marginBottom: 8 }}>
                  Deployment failed
                </div>
                <div style={{ fontSize: 14, color: colors.textMuted, marginBottom: 20 }}>
                  {String(result?.failureMessage ?? 'An unknown error occurred.')}
                </div>
                <div style={{ display: 'flex', gap: 12 }}>
                  <button
                    type="button"
                    onClick={() => setStatus({ phase: 'idle' })}
                    style={{
                      padding: '10px 18px',
                      borderRadius: 8,
                      border: 'none',
                      background: colors.primary,
                      color: '#fff',
                      fontSize: 14,
                      fontWeight: 600,
                      cursor: 'pointer',
                    }}
                  >
                    Retry
                  </button>
                  <button
                    type="button"
                    onClick={() => navigate('/applications')}
                    style={{
                      padding: '10px 18px',
                      borderRadius: 8,
                      border: `1px solid ${colors.border}`,
                      background: colors.surface,
                      color: colors.text,
                      fontSize: 14,
                      fontWeight: 600,
                      cursor: 'pointer',
                    }}
                  >
                    View Details
                  </button>
                  <button
                    type="button"
                    onClick={() => navigate('/')}
                    style={{
                      padding: '10px 18px',
                      borderRadius: 8,
                      border: `1px solid ${colors.border}`,
                      background: colors.surface,
                      color: colors.text,
                      fontSize: 14,
                      fontWeight: 600,
                      cursor: 'pointer',
                    }}
                  >
                    Create New
                  </button>
                </div>
              </div>
            );
          })()
        ) : null}
      </div>
    </PageShell>
  );
}
